import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Box,
  Chip,
  CircularProgress,
  FormHelperText,
  Autocomplete,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
  useGetNotificationTemplatesQuery,
  useGetNotificationChannelsQuery,
  useGetNotificationGroupsQuery,
  useSendNotificationMutation,
} from '../../services/connectApi';
import type { NotificationTemplate } from '../../models';

interface SendNotificationProps {
  open: boolean;
  onClose: () => void;
}

const SendNotification: React.FC<SendNotificationProps> = ({ open, onClose }) => {
  const [templateId, setTemplateId] = useState('');
  const [channelIds, setChannelIds] = useState<string[]>([]);
  const [groupIds, setGroupIds] = useState<string[]>([]);
  const [userIds, setUserIds] = useState<string[]>([]);
  const [variables, setVariables] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] = useState('');

  const { data: templatesData, isLoading: templatesLoading } = useGetNotificationTemplatesQuery({page: 1, page_size: 100});
  const templates = templatesData?.results;
  const { data: channelsData, isLoading: channelsLoading } = useGetNotificationChannelsQuery({page: 1, page_size: 100});
  const channels = channelsData?.results;
  const { data: groupsData, isLoading: groupsLoading } = useGetNotificationGroupsQuery({page: 1, page_size: 100});
  const groups = groupsData?.results;
  const [sendNotification] = useSendNotificationMutation();

  const [selectedTemplate, setSelectedTemplate] = useState<NotificationTemplate | null>(null);

  useEffect(() => {
    if (templates && templateId) {
      const template = templates.find(t => t.id === templateId);
      setSelectedTemplate(template || null);
      
      // Initialize variables with empty values
      if (template && template.variables.length > 0) {
        const initialVariables: Record<string, string> = {};
        template.variables.forEach(variable => {
          initialVariables[variable] = '';
        });
        setVariables(initialVariables);
      } else {
        setVariables({});
      }
    }
  }, [templates, templateId]);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const resetForm = () => {
    setTemplateId('');
    setChannelIds([]);
    setGroupIds([]);
    setUserIds([]);
    setVariables({});
    setErrors({});
    setSuccessMessage('');
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!templateId) {
      newErrors.template = 'Template is required';
    }
    
    if (channelIds.length === 0) {
      newErrors.channels = 'At least one channel is required';
    }
    
    if (groupIds.length === 0 && userIds.length === 0) {
      newErrors.recipients = 'At least one group or user is required';
    }
    
    if (selectedTemplate && selectedTemplate.variables.length > 0) {
      selectedTemplate.variables.forEach(variable => {
        if (!variables[variable]) {
          newErrors[`variable_${variable}`] = `${variable} is required`;
        }
      });
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSend = async () => {
    if (!validateForm()) return;
    
    try {
      const result = await sendNotification({
        template: templateId,
        channels: channelIds,
        groups: groupIds.length > 0 ? groupIds : undefined,
        users: userIds.length > 0 ? userIds : undefined,
        variables: Object.keys(variables).length > 0 ? variables : undefined,
      }).unwrap();
      
      setSuccessMessage(`Successfully sent to ${result.sent_count} recipients`);
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (error) {
      console.error('Failed to send notification:', error);
      setErrors({
        submit: 'Failed to send notification. Please try again.',
      });
    }
  };

  const handleVariableChange = (variable: string, value: string) => {
    setVariables(prev => ({
      ...prev,
      [variable]: value,
    }));
    
    // Clear error for this variable if it exists
    if (errors[`variable_${variable}`]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`variable_${variable}`];
        return newErrors;
      });
    }
  };

  const isLoading = templatesLoading || channelsLoading || groupsLoading;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="send-notification-dialog-title"
    >
      <DialogTitle id="send-notification-dialog-title">
        Send Immediate Notification
      </DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {successMessage && (
              <Grid item xs={12}>
                <Box sx={{ bgcolor: 'success.light', color: 'success.contrastText', p: 2, borderRadius: 1 }}>
                  <Typography variant="body1">{successMessage}</Typography>
                </Box>
              </Grid>
            )}

            {errors.submit && (
              <Grid item xs={12}>
                <Box sx={{ bgcolor: 'error.light', color: 'error.contrastText', p: 2, borderRadius: 1 }}>
                  <Typography variant="body1">{errors.submit}</Typography>
                </Box>
              </Grid>
            )}

            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.template}>
                <InputLabel id="template-select-label">Notification Template</InputLabel>
                <Select
                  labelId="template-select-label"
                  value={templateId}
                  label="Notification Template"
                  onChange={(e) => setTemplateId(e.target.value)}
                >
                  {templates?.map((template) => (
                    <MenuItem key={template.id} value={template.id}>
                      {template.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.template && <FormHelperText>{errors.template}</FormHelperText>}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.channels}>
                <InputLabel id="channel-select-label">Notification Channels</InputLabel>
                <Select
                  labelId="channel-select-label"
                  multiple
                  value={channelIds}
                  label="Notification Channels"
                  onChange={(e) => setChannelIds(e.target.value as string[])}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const channel = channels?.find(c => c.id === value);
                        return channel ? (
                          <Chip key={value} label={channel.name} />
                        ) : null;
                      })}
                    </Box>
                  )}
                >
                  {channels?.filter(channel => channel.is_active).map((channel) => (
                    <MenuItem key={channel.id} value={channel.id}>
                      {channel.name} ({channel.type})
                    </MenuItem>
                  ))}
                </Select>
                {errors.channels && <FormHelperText>{errors.channels}</FormHelperText>}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.recipients}>
                <InputLabel id="group-select-label">Recipient Groups</InputLabel>
                <Select
                  labelId="group-select-label"
                  multiple
                  value={groupIds}
                  label="Recipient Groups"
                  onChange={(e) => setGroupIds(e.target.value as string[])}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const group = groups?.find(g => g.id === value);
                        return group ? (
                          <Chip key={value} label={`${group.name} (${group.users_count} users)`} />
                        ) : null;
                      })}
                    </Box>
                  )}
                >
                  {groups?.map((group) => (
                    <MenuItem key={group.id} value={group.id}>
                      {group.name} ({group.users_count} users)
                    </MenuItem>
                  ))}
                </Select>
                {errors.recipients && groupIds.length === 0 && userIds.length === 0 && (
                  <FormHelperText>{errors.recipients}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={[]} // Would be replaced with actual user options in a real implementation
                freeSolo
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Individual User IDs"
                    placeholder="Type user ID and press Enter"
                    helperText="Enter user IDs manually. Each ID should be entered separately."
                  />
                )}
                value={userIds}
                onChange={(_, newValue) => setUserIds(newValue)}
              />
            </Grid>

            {selectedTemplate && selectedTemplate.variables.length > 0 && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ mt: 2 }}>
                    Template Variables
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Fill in the values for the template variables
                  </Typography>
                </Grid>

                {selectedTemplate.variables.map((variable) => (
                  <Grid item xs={12} key={variable}>
                    <TextField
                      label={variable}
                      fullWidth
                      value={variables[variable] || ''}
                      onChange={(e) => handleVariableChange(variable, e.target.value)}
                      error={!!errors[`variable_${variable}`]}
                      helperText={errors[`variable_${variable}`] || ''}
                    />
                  </Grid>
                ))}
              </>
            )}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSend}
          variant="contained"
          color="primary"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
        >
          {isLoading ? 'Sending...' : 'Send Notification'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendNotification;
